import type { CSSProperties, ReactNode, RefObject } from 'react';

import type { AnimatedModalContent } from '@/components/adg4/animated-modal/src/animated-modal';

export type NodeShape = 'circle' | 'pill' | 'rectangle' | 'hexagon' | 'triangle';

/**
 * How `bgImage` is drawn when both `bgImage` and `text` are set on a `pill` or
 * `rectangle` node. Other shapes ignore this and always behave like `background`.
 */
export type NodeImagePlacement = 'background' | 'insideLeading' | 'outsideLeading';

export type ConnectorShape = 'arced' | 'straight' | 'curvy' | 'orthogonal' | 'mixed';

export type ConnectorType = 'solid' | 'dashed' | 'dotted';

export type ConnectorBias = 'positive' | 'negative';

export type AnimationColor = 'lime' | 'purple' | 'blue' | 'yellow' | 'neutral';

export const usableColors = [
	'white',
	'black',
	'secondarynavy',
	'secondarygreen',
	'secondaryred',
	'transparent',
	'blue100',
	'blue200',
	'blue300',
	'blue400',
	'blue500',
	'blue600',
	'blue700',
	'blue800',
	'blue900',
	'lime100',
	'lime200',
	'lime300',
	'lime400',
	'lime500',
	'lime600',
	'lime700',
	'lime800',
	'lime900',
	'neutral100',
	'neutral200',
	'neutral300',
	'neutral400',
	'neutral500',
	'neutral600',
	'neutral700',
	'neutral800',
	'neutral900',
	'neutral1000',
	'neutral1100',
	'orange100',
	'orange200',
	'orange300',
	'orange400',
	'orange500',
	'orange600',
	'orange700',
	'orange800',
	'orange900',
	'purple100',
	'purple200',
	'purple300',
	'purple400',
	'purple500',
	'purple600',
	'purple700',
	'purple800',
	'purple900',
	'saffron100',
	'saffron200',
	'saffron300',
	'saffron400',
	'saffron500',
	'saffron600',
	'saffron700',
	'saffron800',
	'saffron900',
	'yellow100',
	'yellow200',
	'yellow300',
	'yellow400',
	'yellow500',
	'yellow600',
	'yellow700',
	'yellow800',
	'yellow900',
] as const;

export type FillColor = (typeof usableColors)[number];

export type SpiderWebNodeBorderColor = FillColor | 'rainbowRovo';

export type { AnimatedModalContent as ModalContent } from '@/components/adg4/animated-modal/src/animated-modal';

export type OverlapFill =
	| FillColor
	| {
			color?: FillColor;
			backgroundImage?: string;
	  };

export type WebNodeOverlapConfig = {
	elementIdToOverlap: string;
	color?: FillColor;
	backgroundImage?: string;
};

export const ANIMATION_PHASES_SCROLL_TIMELINE_UNITS_DEFAULT = 100;

export type AnimationPhasesDrive = 'time' | 'scroll';

export type AnimationPhase = {
	delay: number;
	duration: number;
	x?: number;
	y?: number;
	opacity?: number;
	size?: number;
	width?: number;
	height?: number;
	scrollWindowStart?: number;
	scrollWindowEnd?: number;
};

export type WebNodeConfig = {
	id: string;
	x: number;
	y: number;
	shape: NodeShape;
	size?: number;
	width?: number;
	height?: number;
	borderRadius?: number;
	bgColor?: FillColor;
	borderColor?: SpiderWebNodeBorderColor;
	borderWidth?: number;
	bgImage?: string;
	imagePlacement?: NodeImagePlacement;
	fontFamily?: 'charlie' | 'monospace';
	fontWeight?: CSSProperties['fontWeight'];
	text?: string;
	htmlContent?: ReactNode;
	textColor?: FillColor;
	opacity?: number;
	rotate?: number;
	mixBlendMode?: CSSProperties['mixBlendMode'];
	modalContent?: AnimatedModalContent;
	overlapColors?: WebNodeOverlapConfig[];
	animationPhases?: AnimationPhase[];
};

export type ConnectorAnimationEffect = 'travelingShapes' | 'shimmer';

export type WebConnectorConfig = {
	from: string;
	to: string;
	connectorShape?: ConnectorShape;
	connectorType?: ConnectorType;
	connectorBias?: ConnectorBias;
	isAnimated?: boolean;
	animationEffect?: ConnectorAnimationEffect;
	color?: FillColor;
	animationColor?: AnimationColor;
};

export type ResolvedConnector = {
	from: WebNodeConfig;
	to: WebNodeConfig;
	connectorShape: ConnectorShape;
	connectorType: ConnectorType;
	index: number;
	connectorBias?: ConnectorBias;
	isAnimated?: boolean;
	animationEffect?: ConnectorAnimationEffect;
	color?: FillColor;
	animationColor?: AnimationColor;
};

export type SpiderWebCanvasClickPct = { x: number; y: number };

export type SpiderWebNodeFocusVariant = 'node-edit' | 'connector';

export type SpiderWebNodeFocus = {
	nodeId: string;
	variant: SpiderWebNodeFocusVariant;
};

export type SpiderWebContainerProps = {
	nodes: WebNodeConfig[];
	/**
	 * @deprecated Use `useViewportDimensions` instead.
	 * Previously capped the container to `max-height: 90vh`.
	 */
	maxHeightViewport?: boolean;
	useViewportDimensions?: boolean;
	connectors?: WebConnectorConfig[];
	useOnlyExplicitConnectors?: boolean;
	connectorShape?: ConnectorShape;
	connectorType?: ConnectorType;
	bgColor?: FillColor;
	width?: number | string;
	height?: number | string;
	maxNeighbors?: number;
	connectorColor?: FillColor;
	connectorWidth?: number;
	fontFamily?: 'charlie' | 'monospace';
	animateOnLoad?: boolean;
	loadEntranceSpeedMultiplier?: number;
	replayOnVisible?: boolean;
	showAnimationPauseControl?: boolean;
	/**
	 * Adds subtle, continuous node drift that is independent of scroll position.
	 * Connectors and overlap layers follow the moving node coordinates. Disabled
	 * automatically when the user prefers reduced motion.
	 */
	ambientMovement?: boolean;
	animationPhasesDrive?: AnimationPhasesDrive;
	scrollSectionRef?: RefObject<HTMLElement | null>;
	/**
	 * Scroll-magnet anchor points as **percent 0–100** of section scroll-through
	 * (same scale as `scrollWindowStart` / `scrollWindowEnd` on nodes). Converted
	 * internally to 0–1 to match `computeDocumentSectionScrollProgress`.
	 */
	scrollMagnetAnchors?: readonly number[];
	/**
	 * Minimum distance from **any** anchor before a settle nudge runs
	 * (progress 0–1). Defaults to `0.004`.
	 */
	scrollMagnetMinProgressDelta?: number;
	/**
	 * Milliseconds with no `scroll` events before a magnet nudge may run; then
	 * one frame checks scroll position is unchanged. Default `100`.
	 */
	scrollMagnetSettleDebounceMs?: number;
	/**
	 * Duration in ms for the scroll-magnet ease (ease-in-out cubic). Higher is
	 * more gradual. Omitted values use the hook default.
	 */
	scrollMagnetDurationMs?: number;
	/**
	 * Minimum scroll delta (px) to treat as intentional direction for magnet anchor
	 * pick; smaller movement uses nearest anchor by absolute progress.
	 */
	scrollMagnetDirectionDeadbandPx?: number;
	/**
	 * Bias applied only when scroll intent is within deadband and a prior intent
	 * direction exists. `0` keeps absolute-nearest behavior; `1` maximally
	 * penalizes anchors opposite that prior direction. Defaults to `0`.
	 */
	scrollMagnetDirectionBias?: number;
	/**
	 * When `animationPhasesDrive` is `"scroll"`, use this progress (0–1) instead
	 * of measuring `scrollSectionRef`. Disables scroll listeners and scroll
	 * magnet for that container. Use to freeze the graph (e.g. at `1`) while the
	 * layout no longer matches the original scroll track.
	 */
	scrollPhaseProgressOverride?: number | null;
	/**
	 * Fires whenever the effective scroll-phase progress changes.
	 * Value is normalized to 0–1 and is the exact value used to evaluate
	 * `scrollWindowStart` / `scrollWindowEnd`.
	 */
	onScrollPhaseProgressChange?: (progress: number) => void;
	onSvgCanvasClick?: (pct: SpiderWebCanvasClickPct) => void;
	onNodeClick?: (node: WebNodeConfig) => void;
	focusedConnector?: { from: string; to: string };
	nodeFocus?: SpiderWebNodeFocus;
};
